/**
 * Benchmark Alerts Component
 * ==========================
 * Automatically notifies owners when business metrics 
 * fall below industry benchmarks with actionable insights
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle, TrendingDown, TrendingUp, ChevronRight, 
  ChevronDown, ChevronUp, X, Lightbulb, Target, DollarSign,
  Users, BarChart3, ArrowRight, CheckCircle2, Clock, Shield
} from 'lucide-react';
import { DataSourceBadge } from '@/components/ui/DataSourceBadge';
import MetricGamePlanModal from '@/components/common/MetricGamePlanModal';
import SourceBadge from '@/components/common/SourceBadge';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';
import { useAuth } from '@/contexts/AuthContext';

// Alert severity levels
const SEVERITY = {
  CRITICAL: 'critical',    // > 15% below benchmark
  WARNING: 'warning',      // 5-15% below benchmark  
  INFO: 'info',           // 0-5% below benchmark
};

// Improvement suggestions by metric type
const IMPROVEMENT_TIPS = {
  gross_profit_margin: [
    { tip: 'Review supplier contracts - negotiate volume discounts', impact: 'High', timeframe: '1-2 weeks' },
    { tip: 'Analyze menu/product pricing vs costs', impact: 'High', timeframe: 'Immediate' },
    { tip: 'Reduce waste and spoilage with inventory tracking', impact: 'Medium', timeframe: '2-4 weeks' }
  ],
  net_profit_margin: [
    { tip: 'Audit fixed costs - rent, utilities, subscriptions', impact: 'High', timeframe: '1 month' },
    { tip: 'Review staffing levels vs revenue patterns', impact: 'High', timeframe: '2 weeks' },
    { tip: 'Consolidate vendors for better pricing', impact: 'Medium', timeframe: '1 month' }
  ],
  wage_to_revenue: [
    { tip: 'Implement AI-powered scheduling to match demand', impact: 'High', timeframe: '2-4 weeks' },
    { tip: 'Cross-train staff for multi-role flexibility', impact: 'Medium', timeframe: '1-2 months' },
    { tip: 'Review overtime policies and approval processes', impact: 'Medium', timeframe: 'Immediate' }
  ],
  customer_retention: [
    { tip: 'Launch loyalty program with tiered rewards', impact: 'High', timeframe: '2-4 weeks' },
    { tip: 'Implement automated follow-up communications', impact: 'Medium', timeframe: '1 week' },
    { tip: 'Gather and act on customer feedback', impact: 'High', timeframe: 'Ongoing' }
  ],
  inventory_turnover: [
    { tip: 'Implement ABC analysis to focus on high-value items', impact: 'High', timeframe: '1-2 weeks' },
    { tip: 'Set up automatic reorder points', impact: 'High', timeframe: '2 weeks' },
    { tip: 'Clear dead stock with promotions or bundles', impact: 'Medium', timeframe: 'Immediate' }
  ],
  occupancy_rate: [
    { tip: 'Dynamic pricing for off-peak periods', impact: 'High', timeframe: '1 week' },
    { tip: 'Partner with local events and businesses', impact: 'Medium', timeframe: '2-4 weeks' },
    { tip: 'Optimize OTA listings and reviews', impact: 'High', timeframe: 'Ongoing' }
  ],
  utilization_rate: [
    { tip: 'Review project scoping and estimation accuracy', impact: 'High', timeframe: '2 weeks' },
    { tip: 'Reduce non-billable administrative time', impact: 'Medium', timeframe: '1 month' },
    { tip: 'Implement better resource allocation tools', impact: 'High', timeframe: '1 month' }
  ]
};

// Default tips for metrics without specific suggestions
const DEFAULT_TIPS = [
  { tip: 'Analyze historical trends to identify root causes', impact: 'Medium', timeframe: '1 week' },
  { tip: 'Benchmark against top performers in your region', impact: 'Medium', timeframe: '2 weeks' },
  { tip: 'Schedule a strategy review with your team', impact: 'Low', timeframe: 'This week' }
];

// Determine severity based on difference from benchmark
const getSeverity = (difference, goodDirection = 'higher') => {
  const adjustedDiff = goodDirection === 'lower' ? -difference : difference;
  
  if (adjustedDiff <= -15) return SEVERITY.CRITICAL;
  if (adjustedDiff <= -5) return SEVERITY.WARNING;
  if (adjustedDiff < 0) return SEVERITY.INFO;
  return null; // Above benchmark - no alert needed
};

// Severity styling
const severityConfig = {
  [SEVERITY.CRITICAL]: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: AlertTriangle,
    iconColor: 'text-red-500',
    badge: 'bg-red-100 text-red-700 border-red-200',
    label: 'Critical',
    description: 'Significantly below industry average'
  },
  [SEVERITY.WARNING]: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    badge: 'bg-amber-100 text-amber-700 border-amber-200',
    label: 'Warning',
    description: 'Below industry average'
  },
  [SEVERITY.INFO]: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    icon: Target,
    iconColor: 'text-blue-500',
    badge: 'bg-blue-100 text-blue-700 border-blue-200',
    label: 'Opportunity',
    description: 'Slightly below industry average'
  }
};

// Single alert card with expandable tips
const BenchmarkAlertCard = ({ alert, onDismiss, onOpenGamePlan }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = severityConfig[alert.severity];
  const Icon = config.icon;
  
  const tips = IMPROVEMENT_TIPS[alert.metric_key] || DEFAULT_TIPS;
  
  return (
    <Card className={`${config.bg} ${config.border} border shadow-sm overflow-hidden`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0 border ${config.border}`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-slate-900 text-sm">{alert.metric_label}</h4>
                <Badge className={`text-[10px] ${config.badge}`}>
                  {config.label}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                onClick={() => onDismiss(alert.id)}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            
            {/* Metric comparison */}
            <div className="flex items-center gap-4 mb-2">
              <div>
                <span className="text-lg font-bold text-slate-900">{alert.your_value}%</span>
                <span className="text-xs text-slate-500 ml-1">Your business</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300" />
              <div>
                <span className="text-lg font-bold text-emerald-600">{alert.benchmark}%</span>
                <span className="text-xs text-slate-500 ml-1">Industry avg</span>
              </div>
              <Badge variant="outline" className={`text-xs ${alert.difference < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                <TrendingDown className="w-3 h-3 mr-1" />
                {Math.abs(alert.difference).toFixed(1)}% below
              </Badge>
            </div>
            
            {/* Source attribution */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] text-slate-500">Source:</span>
              <SourceBadge sourceId={(alert.source || 'ATO').toUpperCase() === 'ABS' ? 'abs_8165' : 'ato_benchmarks'} compact />
              <span className="text-[10px] text-slate-400">• {alert.period}</span>
            </div>
            
            {/* Expand/collapse tips */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between text-xs h-8 bg-white/50 hover:bg-white"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span className="flex items-center gap-1">
                <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                {tips.length} ways to improve
              </span>
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
            
            {/* Expanded tips */}
            {isExpanded && (
              <div className="mt-3 space-y-2">
                {tips.map((tip, idx) => (
                  <div 
                    key={tip.id || tip.tip || `tip-${idx}`}
                    className="flex items-start gap-2 p-2 bg-white rounded-lg border border-slate-100"
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      tip.impact === 'High' ? 'bg-emerald-100 text-emerald-600' :
                      tip.impact === 'Medium' ? 'bg-blue-100 text-blue-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      <span className="text-[10px] font-bold">{idx + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-700">{tip.tip}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`text-[9px] ${
                          tip.impact === 'High' ? 'text-emerald-600 border-emerald-200' :
                          tip.impact === 'Medium' ? 'text-blue-600 border-blue-200' :
                          'text-slate-600'
                        }`}>
                          {tip.impact} Impact
                        </Badge>
                        <span className="text-[9px] text-slate-400 flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          {tip.timeframe}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* CTA */}
                <Button 
                  size="sm" 
                  className="w-full mt-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid={`benchmark-gameplan-${alert.metric_key}`}
                  onClick={() => onOpenGamePlan(alert)}
                >
                  View game plan & assign
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main component
export const BenchmarkAlerts = ({ 
  businessMetrics = {},
  industry = 'default',
  onDismissAlert,
  onViewDetails,
  className = ''
}) => {
  const { isAuthenticated } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [gamePlanMetric, setGamePlanMetric] = useState(null);

  const lowerIsBetter = (a) => /cost|churn|wait|no.?show|wastage|abandon|shrink|outstanding|stockout|\bdays\b|defect|incident|fuel|dso/i.test(`${a.metric_key} ${a.metric_label}`);
  const handleOpenGamePlan = (alert) => setGamePlanMetric({
    key: alert.metric_key, label: alert.metric_label, value: alert.your_value, benchmark: alert.benchmark,
    unit: '%', goodDirection: lowerIsBetter(alert) ? 'lower' : 'higher', industry,
    sourceId: (alert.source || 'ato').toLowerCase(), sourceNote: `${alert.source} · ${alert.period}`,
  });
  
  // Fetch benchmark comparisons
  useEffect(() => {
    // Defer until auth is ready so we don't fire before the session cookie
    // is established on first paint (avoids "Failed to fetch" console noise).
    if (!isAuthenticated) return;
    const fetchAlerts = async () => {
      try {
        // Iter-318 — `authFetch` reads the HttpOnly cookie; no
        // client-side token gate. Backend gracefully 401s for
        // unauthenticated callers.
        // Build query params from business metrics
        const params = new URLSearchParams();
        if (businessMetrics.grossProfit) params.append('gross_profit', businessMetrics.grossProfit);
        if (businessMetrics.labourCost) params.append('labour_cost', businessMetrics.labourCost);
        if (businessMetrics.netProfit) params.append('net_profit', businessMetrics.netProfit);
        
        const response = await authFetch(
          `${API_URL}/api/benchmarks/compare?${params.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          // Convert comparisons to alerts
          const newAlerts = data.comparisons
            .filter(c => c.status === 'below' || (c.status === 'above' && c.metric.toLowerCase().includes('cost')))
            .map((comparison, idx) => {
              const isLabourCost = comparison.metric.toLowerCase().includes('cost');
              const severity = getSeverity(
                comparison.difference, 
                isLabourCost ? 'lower' : 'higher'
              );
              
              if (!severity) return null;
              
              return {
                id: `alert_${idx}_${Date.now()}`,
                metric_key: comparison.metric.toLowerCase().replace(/ /g, '_'),
                metric_label: comparison.metric,
                your_value: comparison.your_value,
                benchmark: comparison.benchmark,
                difference: comparison.difference,
                severity,
                source: 'ATO',
                period: 'FY2023-24',
                insight: comparison.insight
              };
            })
            .filter(Boolean);
          
          setAlerts(newAlerts);
        }
      } catch (error) {
        console.error('Failed to fetch benchmark alerts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAlerts();
  }, [businessMetrics, isAuthenticated]);
  
  // Handle dismiss
  const handleDismiss = (alertId) => {
    setDismissedAlerts(prev => [...prev, alertId]);
    if (onDismissAlert) onDismissAlert(alertId);
  };
  
  // Filter out dismissed alerts
  const activeAlerts = alerts.filter(a => !dismissedAlerts.includes(a.id));
  
  // Sort by severity (critical first)
  const sortedAlerts = useMemo(() => {
    const severityOrder = { [SEVERITY.CRITICAL]: 0, [SEVERITY.WARNING]: 1, [SEVERITY.INFO]: 2 };
    return [...activeAlerts].sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [activeAlerts]);
  
  // Show max 2 by default
  const displayAlerts = showAll ? sortedAlerts : sortedAlerts.slice(0, 2);
  
  if (isLoading) {
    return (
      <Card className={`border-0 shadow-sm ${className}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-slate-500">
            <div className="w-5 h-5 border-2 border-slate-300 border-t-indigo-500 rounded-full animate-spin" />
            <span className="text-sm">Analyzing your metrics against industry benchmarks...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (activeAlerts.length === 0) {
    return (
      <Card className={`border-0 shadow-sm bg-emerald-50 ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-semibold text-emerald-900 text-sm">All Metrics On Track</h4>
              <p className="text-xs text-emerald-700">Your business is performing at or above industry benchmarks</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const criticalCount = activeAlerts.filter(a => a.severity === SEVERITY.CRITICAL).length;
  const warningCount = activeAlerts.filter(a => a.severity === SEVERITY.WARNING).length;
  
  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            criticalCount > 0 ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            <AlertTriangle className={`w-4 h-4 ${
              criticalCount > 0 ? 'text-red-600' : 'text-amber-600'
            }`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">Benchmark Alerts</h3>
            <p className="text-xs text-slate-500">Metrics below industry averages</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">
              {criticalCount} Critical
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">
              {warningCount} Warning
            </Badge>
          )}
        </div>
      </div>
      
      {/* Alert cards */}
      <div className="space-y-3">
        {displayAlerts.map(alert => (
          <BenchmarkAlertCard 
            key={alert.id}
            alert={alert}
            onDismiss={handleDismiss}
            onOpenGamePlan={handleOpenGamePlan}
          />
        ))}
      </div>

      <MetricGamePlanModal
        open={!!gamePlanMetric}
        onClose={() => setGamePlanMetric(null)}
        metric={gamePlanMetric}
      />
      
      {/* Show more/less */}
      {sortedAlerts.length > 2 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-3 text-xs"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? (
            <>Show Less <ChevronUp className="w-4 h-4 ml-1" /></>
          ) : (
            <>Show {sortedAlerts.length - 2} More Alerts <ChevronDown className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      )}
    </div>
  );
};

export default BenchmarkAlerts;
